import { useState, useEffect } from 'react';
import { FolderTree, Plus, Edit, Trash2, Save, X, Loader2, Search, ChevronRight } from 'lucide-react';
import { categoryAPI } from '../services/api';
import { toast } from 'sonner';

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', parent: '', applicable_to: 'all' });
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await categoryAPI.list();
      setCategories(res.data?.results || res.data || []);
    } catch { setCategories([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const handleSave = async () => {
    if (!form.name) { toast.error('Category name required'); return; }
    // Duplicate check: same name at same level (parent categories only)
    if (!editing) {
      const parentVal = form.parent || null;
      const duplicate = categories.find(c =>
        c.name.toLowerCase().trim() === form.name.toLowerCase().trim() &&
        (c.parent || null) === parentVal
      );
      if (duplicate) {
        toast.error(`A ${parentVal ? 'subcategory' : 'category'} named "${form.name}" already exists at this level.`);
        return;
      }
    }
    const data = { name: form.name, description: form.description, applicable_to: form.applicable_to };
    if (form.parent) data.parent = form.parent;
    setSaving(true);
    try {
      if (editing) { await categoryAPI.update(editing, data); toast.success('Category updated'); }
      else { await categoryAPI.create(data); toast.success('Category created'); }
      setShowForm(false); setEditing(null);
      setForm({ name: '', description: '', parent: '', applicable_to: 'all' });
      fetch();
    } catch (err) {
      const detail = err.response?.data?.non_field_errors?.[0] || err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed';
      toast.error(detail.includes('unique') ? `Category "${form.name}" already exists at this level.` : detail);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Subcategories will also be affected.')) return;
    try { await categoryAPI.delete(id); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed — category may be in use'); }
  };

  const startEdit = (c) => {
    setForm({ name: c.name, description: c.description || '', parent: c.parent || '', applicable_to: c.applicable_to || 'all' });
    setEditing(c.id); setShowForm(true);
  };

  // Build tree structure — API returns only parents with nested subcategories
  const parentCats = categories;  // Already filtered to parents by API (view=tree)

  const filtered = search
    ? categories.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || (c.subcategories || []).some(s => s.name.toLowerCase().includes(search.toLowerCase())))
    : null;

  return (
    <div data-testid="categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <FolderTree className="w-6 h-6 text-sky-500" /> Categories Management
          </h2>
          <p className="text-sm text-slate-500 mt-1">Create and organize ticket categories and subcategories</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', description: '', parent: '', applicable_to: 'all' }); }}
          className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 hover:bg-sky-600"><Plus className="w-4 h-4" /> Add Category</button>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search categories..." className="w-full pl-9 pr-4 py-2.5 border dark:border-slate-700 rounded-xl text-sm dark:bg-slate-800 dark:text-white" />
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-6 mb-6">
          <h3 className="font-semibold mb-4 dark:text-white">{editing ? 'Edit' : 'New'} Category</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <input placeholder="Category Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            <input placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white" />
            <select value={form.parent} onChange={e => setForm({...form, parent: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
              <option value="">No Parent (Top Level)</option>
              {parentCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={form.applicable_to} onChange={e => setForm({...form, applicable_to: e.target.value})} className="border dark:border-slate-700 rounded-xl px-3 py-2.5 text-sm dark:bg-slate-900 dark:text-white">
              <option value="all">All Ticket Types</option>
              <option value="incident">Incident Only</option>
              <option value="service_request">Service Request Only</option>
              <option value="change_request">Change Request Only</option>
            </select>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} disabled={saving} className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editing ? 'Update' : 'Create'}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-4 py-2 rounded-xl text-sm"><X className="w-4 h-4 inline mr-1" />Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : (
        <div className="space-y-3">
          {(filtered || parentCats).map(cat => {
            const children = cat.subcategories || [];
            return (
              <div key={cat.id} className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <FolderTree className="w-4 h-4 text-sky-500" />
                    <div>
                      <span className="font-semibold text-slate-800 dark:text-white">{cat.name}</span>
                      {cat.description && <span className="text-xs text-slate-400 ml-2">— {cat.description}</span>}
                    </div>
                    <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded">{cat.applicable_to || 'all'}</span>
                    {children.length > 0 && <span className="text-xs text-sky-600 dark:text-sky-400">{children.length} subcategories</span>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(cat)} className="text-slate-400 hover:text-sky-600 p-1"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(cat.id)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {children.length > 0 && (
                  <div className="border-t dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30 px-5 py-2">
                    <div className="flex flex-wrap gap-2">
                      {children.map(sub => (
                        <div key={sub.id} className="flex items-center gap-1.5 bg-white dark:bg-slate-800 border dark:border-slate-700 px-3 py-1.5 rounded-lg text-sm">
                          <ChevronRight className="w-3 h-3 text-slate-400" />
                          <span className="dark:text-slate-300">{sub.name}</span>
                          <button onClick={() => startEdit(sub)} className="text-slate-400 hover:text-sky-600"><Edit className="w-3 h-3" /></button>
                          <button onClick={() => handleDelete(sub.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {(filtered || parentCats).length === 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-12 text-center">
              <FolderTree className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No categories found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
